/**
 * RoleAssignmentModal Component
 *
 * Reusable modal for assigning or editing role assignments.
 * Handles both "Assign Role" and "Edit Role Assignment" flows.
 * Includes permissions management section.
 */

import { useState, useEffect, useMemo } from 'react';
import { runInBackground } from '@/lib/utils';
import { getOrganizationCapacityLabel } from '@/lib/organization-capacity';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { RoleAssignmentPermissionsSection } from './RoleAssignmentPermissionsSection';
import { ManagePermissionsModal } from '@/components/profile/ManagePermissionsModal';
import { RevokePermissionModal } from '@/components/profile/RevokePermissionModal';
import {
    Role,
    isOrganizationalRole,
} from '@pillar-portal/database/dist/frontend';
import { RoleSchema } from '@pillar-portal/api-types';
import type { PermissionsGrant } from '@pillar-portal/api-types';
import type {
    OrganizationListItem,
    OrganizationOmnisearchListItem,
    OrganizationOmnisearchSearchRequest,
    OrganizationOmnisearchPageResponse,
} from '@pillar-portal/api-types';
import { ALL_AUTHORIZABLE_ACTIONS } from '@pillar-portal/database/dist/frontend';
import {
    OmnisearchSelect,
    type OmnisearchFetchParams,
    type OmnisearchPage,
} from '@/components/ui/omnisearch-select';

const parseRoleValue = (value: string): Role | null => {
    const parseResult = RoleSchema.safeParse(value);
    if (!parseResult.success) {
        return null;
    }
    return parseResult.data;
};

export type RoleAssignmentModalMode = 'assign' | 'edit';

export interface RoleAssignmentModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    mode: RoleAssignmentModalMode;

    // For assign mode
    userEmail?: string;
    userDisplayName?: string;

    // For edit mode
    roleAssignmentId?: number;
    roleAssignmentUuid?: string;

    // Initial values
    initialRole: Role | '';
    initialOrganizationUuid?: string | null;

    // Available options
    availableRoles: Role[];
    availableOrganizations: OrganizationListItem[];
    organizationSearch?: (
        params: OrganizationOmnisearchSearchRequest
    ) => Promise<OrganizationOmnisearchPageResponse>;

    // Callbacks
    onSave: (data: { role: Role; organization_uuid?: string | null }) => void | Promise<void>;
    onCancel?: () => void;

    // State
    isLoading?: boolean;
    error?: Error | null;

    // After assignment, the role assignment ID becomes available
    assignedRoleAssignmentId?: number | null;
    assignedRoleAssignmentUuid?: string | null;
}

export function RoleAssignmentModal({
    open,
    onOpenChange,
    mode,
    userEmail,
    userDisplayName,
    roleAssignmentUuid,
    initialRole,
    initialOrganizationUuid = null,
    availableRoles,
    availableOrganizations,
    organizationSearch,
    onSave,
    onCancel,
    isLoading = false,
    error,
    assignedRoleAssignmentId,
    assignedRoleAssignmentUuid,
}: RoleAssignmentModalProps) {
    const [role, setRole] = useState<Role | ''>(initialRole);
    const [organizationId, setOrganizationId] = useState<string>(initialOrganizationUuid ?? '');
    const [showManagePermissions, setShowManagePermissions] = useState(false);
    const [showRevokePermission, setShowRevokePermission] = useState(false);
    const [permissionToRevoke, setPermissionToRevoke] = useState<PermissionsGrant | null>(null);
    const [editingPermission, setEditingPermission] = useState<PermissionsGrant | null>(null);

    // Reset form when modal opens/closes or initial values change
    // But don't reset role if we already have an assignedRoleAssignmentId (permissions section is showing)
    useEffect(() => {
        if (open && !assignedRoleAssignmentId) {
            setRole(initialRole);
            setOrganizationId(initialOrganizationUuid ?? '');
        }
    }, [open, initialRole, initialOrganizationUuid, assignedRoleAssignmentId]);

    const selectedOrganization = useMemo(() => {
        if (organizationId.length === 0) {
            return null;
        }
        return (
            availableOrganizations.find(
                (organization) =>
                    organization.organization_uuid === organizationId ||
                    organization.organization_id.toString() === organizationId
            ) ?? null
        );
    }, [availableOrganizations, organizationId]);

    // Clear role if it becomes invalid when organization changes (assign mode)
    useEffect(() => {
        if (mode === 'assign' && organizationId && role) {
            const selectedOrg = selectedOrganization;
            if (selectedOrg) {
                const validRoles = availableRoles;
                if (validRoles.length > 0 && !validRoles.includes(role)) {
                    setRole('');
                } else if (validRoles.length === 1) {
                    const firstRole = validRoles[0];
                    if (firstRole && role !== firstRole) {
                        setRole(firstRole);
                    }
                }
            }
        }
    }, [availableRoles, mode, organizationId, role, selectedOrganization]);

    // Determine which role assignment ID to use for permissions
    const permissionsRoleAssignmentUuid =
        mode === 'edit' ? roleAssignmentUuid : assignedRoleAssignmentUuid;

    // // Determine current role for permissions modals
    // // In assign mode, use the role from state (which should be preserved after assignment)
    // // In edit mode, use role from state or initialRole
    // const currentRole: Role =
    //     mode === 'edit'
    //         ? role || initialRole
    //         : role || (assignedRoleAssignmentId ? initialRole : '');

    const currentRole: Role | '' = role || initialRole;

    // Determine current organization ID for permissions modals
    const currentOrganizationId =
        mode === 'edit'
            ? (availableOrganizations.find(
                  (organization) => organization.organization_uuid === initialOrganizationUuid
              )?.organization_id ?? null)
            : selectedOrganization?.organization_id ?? null;

    const currentOrganizationUuid: string | null =
        mode === 'edit'
            ? initialOrganizationUuid
            : selectedOrganization?.organization_uuid ?? null;

    const isNestedPermissionsModalOpen: boolean =
        showManagePermissions || showRevokePermission;
    const roleAssignmentDialogOpen: boolean = open && !isNestedPermissionsModalOpen;

    // Filter roles based on user type and organization type
    const filteredRoles = useMemo(
        () => availableRoles,
        [
            availableRoles,
        ]
    );

    const filteredOrganizations = useMemo(() => {
        return availableOrganizations;
    }, [availableOrganizations]);

    const organizationOmnisearchFetchPage = useMemo(() => {
        if (!organizationSearch) {
            return null;
        }
        const fetchPage = async (
            params: OmnisearchFetchParams<string>
        ): Promise<OmnisearchPage<OrganizationOmnisearchListItem, string>> => {
            const response = await organizationSearch({
                ...(params.terms !== undefined ? { terms: [...params.terms] } : {}),
                limit: params.limit,
                cursor: params.cursor,
            });
            return { items: response.organizations, pagination: response.pagination };
        };
        return fetchPage;
    }, [organizationSearch]);

    const handleSave = () => {
        if (!role) return;

        const parsedRole = parseRoleValue(role);
        if (!parsedRole) {
            throw new Error(`Invalid role: ${role}`);
        }

        const data: {
            role: Role;
            organization_uuid?: string | null;
        } = {
            role: parsedRole,
        };

        if (mode === 'assign') {
            if (
                isOrganizationalRole &&
                isOrganizationalRole(role) &&
                currentOrganizationUuid !== null
            ) {
                data.organization_uuid = currentOrganizationUuid;
            } else if (!isOrganizationalRole || !isOrganizationalRole(role)) {
                data.organization_uuid = null;
            }
        } else {
            data.organization_uuid = null;
        }

        const result = onSave(data);
        if (result instanceof Promise) {
            runInBackground(result, (err) => {
                console.error('Error saving role assignment', err);
            });
        }
    };

    const handleCancel = () => {
        if (onCancel) {
            onCancel();
        }
        onOpenChange(false);
    };

    const canSave =
        !isLoading &&
        !!role &&
        (mode === 'assign'
            ? !isOrganizationalRole || !isOrganizationalRole(role) || !!organizationId
            : true);

    const title = mode === 'assign' ? 'Assign Role' : 'Edit Role Assignment';
    const description =
        mode === 'assign'
            ? `Assign a role to ${userEmail || 'this user'}`
            : 'Update the role or permissions for this assignment.';

    return (
        <>
            {roleAssignmentDialogOpen && (
                <Dialog open={true} onOpenChange={onOpenChange}>
                <DialogContent
                    className="max-w-2xl max-h-[90vh] overflow-y-auto"
                >
                    <DialogHeader>
                        <DialogTitle>{title}</DialogTitle>
                        <DialogDescription>{description}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        {/* Organization Selection - Show when assigning organizational roles */}
                        {/* Show if: user is organizational OR selected role is organizational */}
                        {mode === 'assign' && role && isOrganizationalRole(role) && (
                            <div>
                                <Label htmlFor="organization">Organization</Label>
                                {organizationOmnisearchFetchPage ? (
                                    <div className="mt-2">
                                        <OmnisearchSelect<
                                            OrganizationOmnisearchListItem,
                                            string
                                        >
                                            label="Search organizations"
                                            placeholder="Type 3+ characters..."
                                            pageSize={25}
                                            fetchPage={organizationOmnisearchFetchPage}
                                            getItemId={(item) => item.organization_uuid}
                                            getItemLabel={(item) =>
                                                `${item.name} (${getOrganizationCapacityLabel(item)})`
                                            }
                                            getItemFilterText={(item) =>
                                                `${item.name} ${item.organization_uuid}`
                                            }
                                            selectedItemId={
                                                organizationId.length > 0 ? organizationId : null
                                            }
                                            onClearSelection={() => {
                                                setOrganizationId('');
                                            }}
                                            disabled={
                                                mode === 'assign' && !!assignedRoleAssignmentId
                                            }
                                            onSelect={(item) => {
                                                setOrganizationId(item.organization_uuid);
                                            }}
                                        />
                                    </div>
                                ) : (
                                    <>
                                        <Select
                                            value={organizationId}
                                            onValueChange={setOrganizationId}
                                            disabled={
                                                mode === 'assign' && !!assignedRoleAssignmentId
                                            }
                                        >
                                            <SelectTrigger id="organization">
                                                <SelectValue placeholder="Select organization" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {filteredOrganizations
                                                    .filter(
                                                        (org) =>
                                                            org.organization_uuid !== undefined &&
                                                            org.organization_uuid.length > 0
                                                    )
                                                    .map((org) => (
                                                        <SelectItem
                                                            key={org.organization_id}
                                                            value={org.organization_uuid!}
                                                        >
                                                            {org.name} (
                                                            {getOrganizationCapacityLabel(org)})
                                                        </SelectItem>
                                                    ))}
                                            </SelectContent>
                                        </Select>
                                        {filteredOrganizations.length === 0 && (
                                            <p className="text-xs text-muted-foreground mt-2">
                                                No eligible organizations available for this user.
                                            </p>
                                        )}
                                    </>
                                )}
                            </div>
                        )}

                        {/* Role Selection */}
                        <div className="space-y-2">
                            <Label htmlFor="role">Role</Label>
                            <Select
                                value={role}
                                onValueChange={(value) => {
                                    const parsedRole = parseRoleValue(value);
                                    if (!parsedRole) {
                                        throw new Error(`Invalid role selected: ${value}`);
                                    }
                                    setRole(parsedRole);
                                }}
                                disabled={
                                    isLoading || (mode === 'assign' && !!assignedRoleAssignmentId)
                                }
                            >
                                <SelectTrigger id="role">
                                    <SelectValue placeholder="Select role" />
                                </SelectTrigger>
                                <SelectContent>
                                    {filteredRoles.map((r) => (
                                        <SelectItem key={r} value={r}>
                                            {r.replace(/_/g, ' ')}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Permissions Section - Show when role assignment exists */}
                        {/* In assign mode: show after role is assigned (assignedRoleAssignmentUuid is set) */}
                        {/* In edit mode: always show (roleAssignmentUuid is always set) */}
                        {permissionsRoleAssignmentUuid && currentRole ? (
                                <RoleAssignmentPermissionsSection
                                roleAssignmentUuid={permissionsRoleAssignmentUuid}
                                onAddPermission={() => {
                                    setEditingPermission(null);
                                    setShowManagePermissions(true);
                                }}
                                onEditPermission={(permission) => {
                                    setEditingPermission(permission);
                                    setShowManagePermissions(true);
                                }}
                                onRevokePermission={(permission) => {
                                    setPermissionToRevoke(permission);
                                    setShowRevokePermission(true);
                                }}
                            />
                        ) : mode === 'assign' ? (
                            <div className="space-y-2">
                                <Label>Permissions</Label>
                                <p className="text-sm text-muted-foreground">
                                    Permissions can be configured after the role is assigned.
                                </p>
                            </div>
                        ) : null}

                        {error && (
                            <Alert variant="destructive">
                                <AlertDescription>
                                    {error instanceof Error ? error.message : 'An error occurred'}
                                </AlertDescription>
                            </Alert>
                        )}
                    </div>
                    <DialogFooter>
                        <div className="flex gap-2">
                            <Button variant="outline" onClick={handleCancel} disabled={isLoading}>
                                {mode === 'assign' && permissionsRoleAssignmentUuid
                                    ? 'Close'
                                    : 'Cancel'}
                            </Button>
                            {(!permissionsRoleAssignmentUuid || mode === 'edit') && (
                                <Button onClick={handleSave} disabled={!canSave}>
                                    {isLoading ? 'Saving...' : 'Save'}
                                </Button>
                            )}
                        </div>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            )}

            {/* Manage Permissions Modal */}
            {permissionsRoleAssignmentUuid && currentRole && (
                <ManagePermissionsModal
                    open={showManagePermissions}
                    onOpenChange={(open) => {
                        setShowManagePermissions(open);
                        if (!open) {
                            setEditingPermission(null);
                        }
                    }}
                    roleAssignmentUuid={permissionsRoleAssignmentUuid}
                    role={currentRole}
                    organizationId={currentOrganizationId}
                    organizationUuid={currentOrganizationUuid}
                    eligibleActions={ALL_AUTHORIZABLE_ACTIONS}
                    editingPermission={editingPermission}
                    principalDisplayName={userDisplayName ?? userEmail ?? null}
                />
            )}

            {/* Revoke Permission Modal */}
            {permissionsRoleAssignmentUuid && currentRole && (
                <RevokePermissionModal
                    open={showRevokePermission}
                    onOpenChange={setShowRevokePermission}
                    roleAssignmentUuid={permissionsRoleAssignmentUuid}
                    permission={permissionToRevoke}
                    role={currentRole}
                />
            )}
        </>
    );
}
